// DTOs for cross-context calls into masters, partners and product [04 §2, §4, §6, §8, §9, §14].
import { z } from 'zod';

import {
  BOM_LINE_TYPE,
  CONTRACTOR_OPERATION,
  DEV_APPROVAL_SUBJECT,
  READINESS_STAGE,
} from '../enums/index.ts';
import { businessDateSchema, idSchema } from '../kernel/index.ts';
import {
  currencyCodeSchema,
  qtyDecimalSchema,
  unitRateSchema,
  uomSchema,
  FX_RATE_TYPE,
  fxRateDecimalSchema,
} from '../units/index.ts';
import { gateCheckSchema } from './common.ts';
import { decimalStringSchema, percentSchema, qtyDtoSchema, utcTimestampSchema } from './scalars.ts';

// ---- masters [04 §2.4 F-MD-01..05] ----

export const fxRateQuerySchema = z.strictObject({
  currency: currencyCodeSchema,
  rateType: FX_RATE_TYPE.schema,
  date: businessDateSchema,
});

/** F-MD-01: the row with max effective_from ≤ date; CAD → rate 1 and rateId null. */
export const fxRateDtoSchema = z.strictObject({
  currency: currencyCodeSchema,
  rateType: FX_RATE_TYPE.schema,
  effectiveFrom: businessDateSchema.nullable(),
  rateToBase: fxRateDecimalSchema,
  rateId: idSchema.nullable(),
});

/** F-MD-02 cross rate a→b = fx(a)/fx(b). */
export const crossRateQuerySchema = z.strictObject({
  from: currencyCodeSchema,
  to: currencyCodeSchema,
  rateType: FX_RATE_TYPE.schema,
  date: businessDateSchema,
});

/** F-MD-05 / BR-MAT-001: item-specific factor first, then generic; half-even to 3 dp. */
export const convertQtyInputSchema = z.strictObject({
  qty: qtyDtoSchema,
  to: uomSchema,
  itemId: idSchema.nullable(),
});

/** F-MD-03 due / discount dates from payment terms (dating rules win). */
export const dueDatesQuerySchema = z.strictObject({
  paymentTermsId: idSchema,
  basisDate: businessDateSchema,
});

export const dueDatesDtoSchema = z.strictObject({
  dueDate: businessDateSchema,
  discountDate: businessDateSchema.nullable(),
  discountPct: percentSchema.nullable(),
});

// ---- partners [04 §4 F-PTN-05] ----

export const partnerKindValues = ['customer', 'supplier', 'contractor', 'carrier'] as const;

export const contractorRateQuerySchema = z.strictObject({
  contractorId: idSchema,
  operation: CONTRACTOR_OPERATION.schema,
  categoryId: idSchema.nullable(),
  qty: qtyDecimalSchema,
  date: businessDateSchema,
});

export const contractorRateDtoSchema = z.strictObject({
  rateId: idSchema,
  unitRate: unitRateSchema,
  effectiveFrom: businessDateSchema,
  effectiveTo: businessDateSchema.nullable(),
});

/** Snapshot data for documents in other contexts; business identity only, no contact data [MB D18]. */
export const partnerSummaryDtoSchema = z.strictObject({
  id: idSchema,
  kind: z.enum(partnerKindValues),
  code: z.string().min(1),
  name: z.string().min(1),
  currency: currencyCodeSchema.nullable(),
  paymentTermsId: idSchema.nullable(),
  isActive: z.boolean(),
});

// ---- product [04 §6, §8, §9, §14] ----

/** SKU snapshot copied onto order/shipment lines (BR-SHP-012); item no. = 24 chars [MB D6]. */
export const skuSnapshotDtoSchema = z.strictObject({
  skuId: idSchema,
  itemNo: z.string().length(24),
  styleId: idSchema,
  styleMasterCode: z.string().length(17),
  colourwayId: idSchema,
  colourCode: z.string().min(1),
  sizePosition: z.int().positive(),
  sizeLabel: z.string().min(1),
  description: z.string(),
  hsCode: z.string().nullable(),
  originCountry: z
    .string()
    .regex(/^[A-Z]{2}$/)
    .nullable(),
});

export const bomQuerySchema = z.strictObject({
  styleId: idSchema,
  colourwayId: idSchema.nullable(),
});

/** Released BOM consumed by costing (sheet from BOM, D9) and cutting (requirement) [04 §8]. */
export const bomDtoSchema = z.strictObject({
  styleId: idSchema,
  revision: z.int().positive(),
  releasedAt: utcTimestampSchema,
  lines: z.array(
    z.strictObject({
      lineId: idSchema,
      lineType: BOM_LINE_TYPE.schema,
      materialId: idSchema,
      materialColourId: idSchema.nullable(),
      colourwayId: idSchema.nullable(),
      consumption: qtyDtoSchema,
      allowancePct: percentSchema.nullable(),
      placement: z.string().nullable(),
    }),
  ),
});

/** Stages a development requirement can block [04 §9 BR-DEV-003] (kernel enum READINESS_STAGE). */
export const readinessStageValues = READINESS_STAGE.values;

export const readinessQuerySchema = z.strictObject({
  colourwayId: idSchema,
  stage: z.enum(readinessStageValues),
});

/** F-DEV-01 readiness = satisfied ÷ required; gate G9 per development.readiness_gate_mode. */
export const readinessDtoSchema = z.strictObject({
  colourwayId: idSchema,
  stage: z.enum(readinessStageValues),
  required: z.int().nonnegative(),
  satisfied: z.int().nonnegative(),
  missing: z.array(
    z.strictObject({ requirementId: idSchema, subjectType: DEV_APPROVAL_SUBJECT.schema }),
  ),
  gate: gateCheckSchema,
});

/** F-DPP-01 readiness_pct(sku) [04 §14]. */
export const dppReadinessDtoSchema = z.strictObject({
  itemNo: z.string().length(24),
  pct: decimalStringSchema,
  missing: z.array(z.string().min(1)),
});

export type FxRateQuery = z.output<typeof fxRateQuerySchema>;
export type FxRateDto = z.output<typeof fxRateDtoSchema>;
export type CrossRateQuery = z.output<typeof crossRateQuerySchema>;
export type ConvertQtyInput = z.output<typeof convertQtyInputSchema>;
export type DueDatesQuery = z.output<typeof dueDatesQuerySchema>;
export type DueDatesDto = z.output<typeof dueDatesDtoSchema>;
export type ContractorRateQuery = z.output<typeof contractorRateQuerySchema>;
export type ContractorRateDto = z.output<typeof contractorRateDtoSchema>;
export type PartnerSummaryDto = z.output<typeof partnerSummaryDtoSchema>;
export type SkuSnapshotDto = z.output<typeof skuSnapshotDtoSchema>;
export type BomQuery = z.output<typeof bomQuerySchema>;
export type BomDto = z.output<typeof bomDtoSchema>;
export type ReadinessQuery = z.output<typeof readinessQuerySchema>;
export type ReadinessDto = z.output<typeof readinessDtoSchema>;
export type DppReadinessDto = z.output<typeof dppReadinessDtoSchema>;
